/**
 * initProjects — search, tab filtering, and column sorting for the projects listing table.
 *
 * Targets `.project-list` on the page. Does nothing if the section is absent.
 * Filter state is kept in plain variables; the DOM is never queried for state.
 */
export default function initProjects() {
  const section = document.querySelector('.project-list')

  if (!section) return

  const searchInput = section.querySelector('.project-list__search-input')
  const tabButtons  = section.querySelectorAll('.project-tab-btn')
  const sortHeaders = section.querySelectorAll('.project-list__th--sortable')
  const tbody       = section.querySelector('.project-list__tbody')
  const emptyState  = section.querySelector('.project-list__empty')

  if (!searchInput || !tbody || !emptyState) return

  /** @type {string} Active tab filter value — 'all' | 'active' | 'ended' | 'upcoming' */
  let activeFilter = 'all'

  /** @type {string} Current search term (lowercased) */
  let searchTerm = ''

  /** @type {string|null} Column currently sorted — 'start' | 'end' | null */
  let sortColumn = 'start'

  /** @type {'asc'|'desc'} Current sort direction */
  let sortDirection = 'desc'

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  /**
   * Return all `<tr>` rows from `tbody` as an array.
   *
   * @returns {HTMLTableRowElement[]}
   */
  function getRows() {
    return Array.from(tbody.querySelectorAll('tr.project-row'))
  }

  /**
   * Determine whether a single row matches the current filter + search state.
   *
   * @param {HTMLTableRowElement} row
   * @returns {boolean}
   */
  function rowIsVisible(row) {
    const statusMatch = activeFilter === 'all' || row.dataset.status === activeFilter
    const searchMatch = !searchTerm || (row.dataset.name || '').includes(searchTerm)
    return statusMatch && searchMatch
  }

  /**
   * Apply current visibility state to every row and toggle the empty-state notice.
   */
  function applyFilter() {
    const rows    = getRows()
    let   visible = 0

    rows.forEach((row) => {
      const show = rowIsVisible(row)
      row.hidden = !show
      if (show) visible++
    })

    if (visible === 0) {
      const key = 'empty' + activeFilter.charAt(0).toUpperCase() + activeFilter.slice(1)
      const msg = searchTerm
        ? (emptyState.dataset[key + 'Search'] || emptyState.dataset.emptyAllSearch || '').replace('%s', searchTerm)
        : (emptyState.dataset[key] || emptyState.dataset.emptyAll || '')
      emptyState.textContent = msg
      emptyState.hidden = false
    } else {
      emptyState.hidden = true
    }

  }

  /**
   * Sort `tbody` rows by the given column key.
   * Rows hidden by the current filter are moved to the end rather than disturbed.
   *
   * @param {string} column - 'start' | 'end'
   * @param {'asc'|'desc'} direction
   */
  function applySort(column, direction) {
    const rows = getRows()

    rows.sort((a, b) => {
      const valA = a.dataset[column] || ''
      const valB = b.dataset[column] || ''

      if (valA === valB) return 0

      const cmp = valA < valB ? -1 : 1
      return direction === 'asc' ? cmp : -cmp
    })

    rows.forEach((row) => tbody.appendChild(row))
  }

  /**
   * Update `aria-sort` on all sortable headers.
   * The active header receives the current direction; others have the attribute removed.
   *
   * @param {Element} activeHeader
   * @param {'asc'|'desc'} direction
   */
  function updateSortAria(activeHeader, direction) {
    sortHeaders.forEach((th) => {
      if (th === activeHeader) {
        th.setAttribute('aria-sort', direction === 'asc' ? 'ascending' : 'descending')
      } else {
        th.removeAttribute('aria-sort')
      }
    })
  }

  // -------------------------------------------------------------------------
  // Hide tabs with no matching rows
  // -------------------------------------------------------------------------

  const existingStatuses = new Set(getRows().map((row) => row.dataset.status))

  tabButtons.forEach((btn) => {
    const filter = btn.dataset.filter
    if (filter !== 'all' && !existingStatuses.has(filter)) {
      btn.hidden = true
    }
  })

  // -------------------------------------------------------------------------
  // Event: search input
  // -------------------------------------------------------------------------

  searchInput.addEventListener('input', () => {
    searchTerm = searchInput.value.toLowerCase()
    applyFilter()
  })

  // -------------------------------------------------------------------------
  // Event: tab buttons
  // -------------------------------------------------------------------------

  tabButtons.forEach((btn) => {
    btn.addEventListener('click', () => {
      activeFilter = btn.dataset.filter || 'all'

      tabButtons.forEach((b) => {
        const isActive = b === btn
        b.classList.toggle('is-active', isActive)
        b.setAttribute('aria-selected', String(isActive))
      })

      applyFilter()
    })
  })

  // -------------------------------------------------------------------------
  // Event: sortable column headers
  // -------------------------------------------------------------------------

  sortHeaders.forEach((th) => {
    th.addEventListener('click', () => {
      const column = th.dataset.sort

      if (sortColumn === column) {
        sortDirection = sortDirection === 'asc' ? 'desc' : 'asc'
      } else {
        sortColumn    = column
        sortDirection = 'asc'
      }

      updateSortAria(th, sortDirection)
      applySort(sortColumn, sortDirection)
    })
  })

  // Apply initial filter on load
  applyFilter()
}
